import * as React from "react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Button, Col, Row, SegmentedControl } from "components/bootstrap";
import { FetchErrorAlert, RelativeTime, Spinner } from "components/common";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import CreateHealthAlertModal from "./alert/CreateHealthAlertModal";
import HealthDetailsPane from "./HealthDetailsPane";
import HealthInterpretationLegend from "./HealthInterpretationLegend";
import HealthTreePane from "./HealthTreePane";
import useHealthPanel from "./useHealthPanel";
import {
  BodyText,
  EmptyStatePane,
  EmptyStateTitle,
  ModuleContent,
  ModuleHeader,
  ModuleHeaderActions,
  ModuleHeaderMeta,
  ModuleHeaderText,
  ModuleLayout,
  ModuleState,
  ModuleTitle
} from "./HealthModule.styles";
const AllHealthyEmptyState = ({ onShowAllChecks }) => {
  const { t } = useTranslation("enterprise_license");
  return /* @__PURE__ */ React.createElement(EmptyStatePane, null, /* @__PURE__ */ React.createElement(EmptyStateTitle, null, t("health.module.all_healthy_title")), /* @__PURE__ */ React.createElement(BodyText, null, t("health.module.all_healthy_body")), /* @__PURE__ */ React.createElement(Button, { bsStyle: "link", bsSize: "small", onClick: onShowAllChecks }, t("health.module.show_all_checks")));
};
const HealthModule = () => {
  const { t } = useTranslation("enterprise_license");
  const {
    showHealthModule,
    viewMode,
    setViewMode,
    problemsOnly,
    error,
    generatedAt,
    hasReport,
    isFetching,
    isLoading,
    lookup,
    root,
    treeData,
    tree,
    selectedNode,
    selectedPath,
    isRootSelected,
    hasNoVisibleProblems,
    hasHardFailure
  } = useHealthPanel();
  const sendTelemetry = useSendTelemetry("cluster-health");
  const [alertTarget, setAlertTarget] = useState(null);
  const openAlertModal = (node, path) => setAlertTarget({ node, path });
  if (!showHealthModule) return null;
  const reportViewOptions = [
    { value: "problems", label: t("health.module.view_problems") },
    { value: "all", label: t("health.module.view_all") }
  ];
  const fetchErrorMessage = hasReport ? t("health.module.refresh_failed") : t("health.module.fetch_failed");
  const changeViewMode = (mode) => {
    setViewMode(mode);
    sendTelemetry(TELEMETRY_EVENT_TYPE.CLUSTER_HEALTH.VIEW_MODE_TOGGLED, {
      app_action_value: mode
    });
  };
  const renderDetailsContent = () => {
    if (hasNoVisibleProblems) {
      return /* @__PURE__ */ React.createElement(AllHealthyEmptyState, { onShowAllChecks: () => changeViewMode("all") });
    }
    if (isRootSelected) {
      return /* @__PURE__ */ React.createElement(HealthInterpretationLegend, { root, path: selectedPath, onCreateAlert: openAlertModal });
    }
    return /* @__PURE__ */ React.createElement(
      HealthDetailsPane,
      {
        tree,
        selectedNode,
        selectedPath,
        showHealthyChildren: !problemsOnly,
        onCreateAlert: openAlertModal
      }
    );
  };
  return /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(ModuleHeader, null, /* @__PURE__ */ React.createElement(ModuleHeaderText, null, /* @__PURE__ */ React.createElement(ModuleTitle, null, t("health.module.title")), generatedAt ? /* @__PURE__ */ React.createElement(ModuleHeaderMeta, null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "health.module.last_generated",
      components: { relativeTime: /* @__PURE__ */ React.createElement(RelativeTime, { dateTime: generatedAt }) }
    }
  ), isFetching ? t("health.module.refreshing") : null) : null), /* @__PURE__ */ React.createElement(ModuleHeaderActions, { role: "group", "aria-label": t("health.module.view_label") }, /* @__PURE__ */ React.createElement(
    SegmentedControl,
    {
      data: reportViewOptions,
      radius: "sm",
      value: viewMode,
      onChange: changeViewMode
    }
  ))), /* @__PURE__ */ React.createElement(ModuleContent, null, isLoading ? /* @__PURE__ */ React.createElement(ModuleState, null, /* @__PURE__ */ React.createElement(Spinner, { text: t("health.module.loading"), delay: 0 })) : /* @__PURE__ */ React.createElement(React.Fragment, null, error ? /* @__PURE__ */ React.createElement(FetchErrorAlert, { message: fetchErrorMessage, error }) : null, hasHardFailure ? null : /* @__PURE__ */ React.createElement(ModuleLayout, null, /* @__PURE__ */ React.createElement(HealthTreePane, { tree, treeData, lookup, root, viewMode }), renderDetailsContent()))), alertTarget ? /* @__PURE__ */ React.createElement(
    CreateHealthAlertModal,
    {
      show: true,
      onHide: () => setAlertTarget(null),
      node: alertTarget.node,
      path: alertTarget.path
    }
  ) : null));
};
export default HealthModule;
